import { reactions as defaultReactionCatalog } from "../components/reactionConfig.js";

const RTYPE_BY_RICON = defaultReactionCatalog.reduce((acc, reaction) => {
  if (reaction?.rIcon) acc[reaction.rIcon] = reaction.rType;
  return acc;
}, {});

const resolveRTypeFromIcon = (rIcon) => {
  if (rIcon === undefined || rIcon === null || rIcon === "") return null;
  const mapped = RTYPE_BY_RICON[rIcon];
  return typeof mapped === "number" ? mapped : null;
};

const normalizeActorKey = (value) => {
  if (value === undefined || value === null) return "";
  return String(value);
};

const normalizeTargetKey = (value) => {
  if (value === undefined || value === null || value === "") return "";
  return String(value);
};

const toTimestamp = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isIncomingOlder = (current, incoming) => {
  const currentTs = toTimestamp(current?.ts);
  const incomingTs = toTimestamp(incoming?.ts);
  return currentTs !== null && incomingTs !== null && incomingTs < currentTs;
};

const sortActiveReactions = (left, right) => {
  const leftTs = toTimestamp(left?.ts);
  const rightTs = toTimestamp(right?.ts);

  if (leftTs !== null && rightTs !== null && leftTs !== rightTs) {
    return leftTs - rightTs;
  }
  if (leftTs === null && rightTs !== null) return -1;
  if (leftTs !== null && rightTs === null) return 1;

  const actorCompare = normalizeActorKey(left?.uidFrom).localeCompare(
    normalizeActorKey(right?.uidFrom),
  );
  if (actorCompare !== 0) return actorCompare;

  return Number(left?.rType) - Number(right?.rType);
};

const makeReactionKey = (actorKey, rType) => `${actorKey}::${rType}`;

const areReactionListsEqual = (left = [], right = []) => {
  if (left === right) return true;
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    const current = left[index];
    const next = right[index];
    if (
      normalizeActorKey(current?.uidFrom) !== normalizeActorKey(next?.uidFrom) ||
      Number(current?.rType) !== Number(next?.rType) ||
      String(current?.rIcon ?? "") !== String(next?.rIcon ?? "") ||
      normalizeTargetKey(current?.msgId) !== normalizeTargetKey(next?.msgId) ||
      normalizeTargetKey(current?.cliMsgId) !==
        normalizeTargetKey(next?.cliMsgId) ||
      toTimestamp(current?.ts) !== toTimestamp(next?.ts)
    ) {
      return false;
    }
  }

  return true;
};

const matchesReactionTarget = (message, reaction) => {
  const messageMsgId = normalizeTargetKey(message?.msgId);
  const messageCliMsgId = normalizeTargetKey(message?.cliMsgId);
  const reactionMsgId = normalizeTargetKey(reaction?.msgId);
  const reactionCliMsgId = normalizeTargetKey(reaction?.cliMsgId);

  if (messageMsgId && reactionMsgId && messageMsgId === reactionMsgId) {
    return true;
  }

  if (
    messageCliMsgId &&
    reactionCliMsgId &&
    messageCliMsgId === reactionCliMsgId
  ) {
    return true;
  }

  return false;
};

export const normalizeActiveReactions = (reacts = []) => {
  if (!Array.isArray(reacts) || reacts.length === 0) return [];

  const activeByKey = new Map();

  for (const react of reacts) {
    const actorKey = normalizeActorKey(react?.uidFrom);
    if (!actorKey) continue;

    const rawRType = Number(react?.rType);
    if (!Number.isFinite(rawRType)) continue;

    if (rawRType === -1) {
      const removedType = resolveRTypeFromIcon(react?.rIcon);
      if (removedType !== null) {
        const key = makeReactionKey(actorKey, removedType);
        const current = activeByKey.get(key);
        if (!current || !isIncomingOlder(current, react)) {
          activeByKey.delete(key);
        }
      } else {
        for (const k of [...activeByKey.keys()]) {
          if (k.startsWith(`${actorKey}::`)) {
            activeByKey.delete(k);
          }
        }
      }
      continue;
    }

    const key = makeReactionKey(actorKey, rawRType);
    const current = activeByKey.get(key);
    const normalizedReact = { ...react, uidFrom: actorKey };

    if (!current || !isIncomingOlder(current, normalizedReact)) {
      activeByKey.set(key, normalizedReact);
    }
  }

  return Array.from(activeByKey.values()).sort(sortActiveReactions);
};

export const applyReactionEventToReactions = (reacts = [], reactionEvent) =>
  normalizeActiveReactions([...(Array.isArray(reacts) ? reacts : []), reactionEvent]);

export const applyReactionEventsToMessages = (messages, reactionEvents = []) => {
  if (!Array.isArray(messages) || messages.length === 0) return messages;
  if (!Array.isArray(reactionEvents) || reactionEvents.length === 0) return messages;

  let didChange = false;

  const nextMessages = messages.map((message) => {
    let nextMessage = message;
    let currentReacts = normalizeActiveReactions(message?.reacts);

    for (const reactionEvent of reactionEvents) {
      if (!matchesReactionTarget(nextMessage, reactionEvent)) continue;

      const nextReacts = applyReactionEventToReactions(currentReacts, reactionEvent);
      if (areReactionListsEqual(currentReacts, nextReacts)) continue;

      didChange = true;
      currentReacts = nextReacts;
      nextMessage = {
        ...nextMessage,
        reacts: nextReacts,
      };
    }

    return nextMessage;
  });

  return didChange ? nextMessages : messages;
};

export const getReactionBadgeSummary = (
  reacts = [],
  reactionCatalog = defaultReactionCatalog,
) => {
  const activeReactions = normalizeActiveReactions(reacts);
  if (activeReactions.length === 0) {
    return { total: 0, items: [] };
  }

  const countsByType = new Map();

  for (const reaction of activeReactions) {
    const typeKey = String(reaction.rType);
    const current = countsByType.get(typeKey) || {
      rType: reaction.rType,
      rIcon: reaction.rIcon,
      count: 0,
    };
    current.count += 1;
    current.rIcon = reaction.rIcon;
    countsByType.set(typeKey, current);
  }

  const knownItems = reactionCatalog
    .filter((reaction) => countsByType.has(String(reaction.rType)))
    .map((reaction) => ({
      ...reaction,
      count: countsByType.get(String(reaction.rType)).count,
    }));

  const unknownItems = Array.from(countsByType.values())
    .filter(
      (reaction) =>
        !reactionCatalog.some(
          (catalogReaction) => Number(catalogReaction.rType) === Number(reaction.rType),
        ),
    )
    .map((reaction) => ({
      ...reaction,
      icon: reaction.rIcon || "•",
      name: `reaction-${reaction.rType}`,
    }));

  return {
    total: activeReactions.length,
    items: [...knownItems, ...unknownItems],
  };
};

export const hasActiveReactions = (reacts = []) =>
  normalizeActiveReactions(reacts).length > 0;
